import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, X, Trash2, SlidersHorizontal, ShieldAlert } from "lucide-react";

const ENTITY_TYPES = [
  { key: "project", label: "Projects" },
  { key: "task", label: "Tasks" },
  { key: "contact", label: "Contacts" },
  { key: "deal", label: "Deals" },
  { key: "invoice", label: "Invoices" },
  { key: "bill", label: "Bills" },
];

const FIELD_TYPE_LABEL = { text: "Text", number: "Number", date: "Date", select: "Dropdown", checkbox: "Checkbox", url: "URL" };

const EMPTY_FORM = { label: "", field_type: "text", options: "", is_required: false, is_unique: false };

export default function CustomFieldsAdmin() {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.role === "owner";

  const [entityType, setEntityType] = useState("project");
  const [fields, setFields] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/custom-fields/definitions?entity_type=${entityType}&include_inactive=true`);
      setFields(data || []);
    } catch {
      toast.error("Failed to load fields");
    }
  }, [entityType]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.label.trim()) return toast.error("Label is required");
    const options = form.field_type === "select"
      ? form.options.split("\n").map(o => o.trim()).filter(Boolean)
      : [];
    if (form.field_type === "select" && options.length === 0) return toast.error("Add at least one option, one per line");
    setSaving(true);
    try {
      await api.post("/custom-fields/definitions", {
        entity_type: entityType,
        label: form.label,
        field_type: form.field_type,
        options,
        is_required: form.is_required,
        is_unique: form.is_unique,
      });
      toast.success("Field created");
      setShowModal(false);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create field");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (f) => {
    try {
      await api.put(`/custom-fields/definitions/${f.field_id}`, { is_active: !f.is_active });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update field");
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete field "${f.label}"? Any values already saved for it will be removed too.`)) return;
    try {
      await api.delete(`/custom-fields/definitions/${f.field_id}`);
      toast.success("Field deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete field");
    }
  };

  if (!isManager) {
    return (
      <div className="p-10 text-center text-sm text-[#7d8590] flex flex-col items-center gap-2">
        <ShieldAlert className="w-6 h-6" />
        Only workspace owners and admins can manage custom fields.
      </div>
    );
  }

  return (
    <DarkPage><div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><SlidersHorizontal className="w-5 h-5 text-[#7d8590]" /> Custom Fields</h1>
          <p className="text-sm text-[#7d8590] mt-1">Define extra fields for projects, contacts and deals. They show up on the entity's detail page automatically.</p>
        </div>
        <Button onClick={() => { setForm(EMPTY_FORM); setShowModal(true); }} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> New Field
        </Button>
      </div>

      <div className="flex bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden w-fit mb-6 flex-wrap">
        {ENTITY_TYPES.map(t => (
          <button key={t.key} onClick={() => setEntityType(t.key)}
            className={`px-4 py-2 text-sm transition-colors ${entityType === t.key ? "bg-[#1c2128] text-white font-medium" : "text-[#7d8590] hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {fields.length === 0 ? (
        <div className="text-center py-16 text-[#7d8590] text-sm">
          No custom fields defined for {ENTITY_TYPES.find(t => t.key === entityType)?.label.toLowerCase()} yet.
        </div>
      ) : (
        <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#21262d]">
                {["Label", "Key", "Type", "Required", "Unique", "Status", ""].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs text-[#7d8590] font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fields.map(f => (
                <tr key={f.field_id} className="border-b border-[#21262d] last:border-0 group">
                  <td className="px-4 py-3 text-white">{f.label}</td>
                  <td className="px-4 py-3 text-[#7d8590] font-mono text-xs">{f.key}</td>
                  <td className="px-4 py-3 text-[#7d8590]">{FIELD_TYPE_LABEL[f.field_type]}{f.field_type === "select" ? ` (${(f.options || []).length})` : ""}</td>
                  <td className="px-4 py-3 text-[#7d8590]">{f.is_required ? "Yes" : "—"}</td>
                  <td className="px-4 py-3 text-[#7d8590]">{f.is_unique ? "Yes" : "—"}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleActive(f)}
                      className={`text-xs px-2 py-0.5 rounded-full border ${f.is_active ? "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20" : "text-[#7d8590] bg-[#1c2128] border-[#30363d]"}`}>
                      {f.is_active ? "active" : "inactive"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(f)} className="opacity-0 group-hover:opacity-100 text-[#484f58] hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New field for {ENTITY_TYPES.find(t => t.key === entityType)?.label}</h2>
              <button onClick={() => setShowModal(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Label *</label>
                <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. Preferred region"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Type</label>
                <select value={form.field_type} onChange={e => setForm(f => ({ ...f, field_type: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                  {Object.entries(FIELD_TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              {form.field_type === "select" && (
                <div>
                  <label className="text-xs text-[#7d8590] mb-1 block">Options (one per line)</label>
                  <textarea value={form.options} onChange={e => setForm(f => ({ ...f, options: e.target.value }))} rows={4}
                    placeholder={"EMEA\nAPAC\nAmericas"}
                    className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none font-mono" />
                </div>
              )}
              <label className="flex items-center gap-2 text-xs text-[#7d8590]">
                <input type="checkbox" checked={form.is_required} onChange={e => setForm(f => ({ ...f, is_required: e.target.checked }))} />
                Mark as required (shown with an asterisk)
              </label>
              <label className="flex items-center gap-2 text-xs text-[#7d8590]">
                <input type="checkbox" checked={form.is_unique} onChange={e => setForm(f => ({ ...f, is_unique: e.target.checked }))} />
                Mark as unique (no two {ENTITY_TYPES.find(t => t.key === entityType)?.label.toLowerCase()} can share the same value)
              </label>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={create} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
                {saving ? "Creating…" : "Create field"}
              </Button>
              <Button onClick={() => setShowModal(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
